import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import {
  Smartphone,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  ChevronDown,
  Info,
  Send,
} from "lucide-react";
import {
  loadSmsConfig,
  saveSmsConfig,
  verifySmsConfig,
  sendTestSms,
} from "@/services/smsService";

export function TwilioConfiguration() {
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [messagingServiceSid, setMessagingServiceSid] = useState("");
  const [enabled, setEnabled] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [authTokenChanged, setAuthTokenChanged] = useState(false);
  const [originalAuthTokenSet, setOriginalAuthTokenSet] = useState(false);
  const [verified, setVerified] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const { currentTenant } = useTenant();

  useEffect(() => {
    if (currentTenant?.tenant_id) {
      loadConfig();
    }
  }, [currentTenant?.tenant_id]);

  const loadConfig = async () => {
    if (!currentTenant?.tenant_id) return;

    try {
      const { config, authTokenSet } = await loadSmsConfig(
        currentTenant.tenant_id
      );

      if (config) {
        setAccountSid(config.account_sid || "");
        setPhoneNumber(config.phone_number || "");
        setMessagingServiceSid(config.messaging_service_sid || "");
        setEnabled(config.enabled ?? false);
        setVerified(!!config.verified_at);
        setOriginalAuthTokenSet(authTokenSet);
        setAuthToken("");
        setAuthTokenChanged(false);
      }
    } catch (error) {
      console.error("Error loading SMS config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentTenant?.tenant_id) return;

    // Validate required fields
    if (!accountSid.trim()) {
      toast.error("Account SID is required");
      return;
    }
    if (!authTokenChanged && !originalAuthTokenSet) {
      toast.error("Auth Token is required");
      return;
    }
    if (!phoneNumber.trim()) {
      toast.error("Twilio Phone Number is required");
      return;
    }

    // Validate Account SID format
    if (!accountSid.startsWith("AC")) {
      toast.error("Account SID should start with 'AC'");
      return;
    }

    // Validate Messaging Service SID format if provided
    if (messagingServiceSid.trim() && !messagingServiceSid.startsWith("MG")) {
      toast.error("Messaging Service SID should start with 'MG'");
      return;
    }

    setSaving(true);
    try {
      const configToSave: Parameters<typeof saveSmsConfig>[1] = {
        account_sid: accountSid.trim(),
        phone_number: phoneNumber.trim(),
        enabled,
      };

      // Only include auth token if it was changed
      if (authTokenChanged && authToken) {
        configToSave.auth_token = authToken;
      }

      // Only include messaging service SID if provided
      if (messagingServiceSid.trim()) {
        configToSave.messaging_service_sid = messagingServiceSid.trim();
      }

      const result = await saveSmsConfig(currentTenant.tenant_id, configToSave);

      if (result.success) {
        toast.success("SMS configuration saved successfully");
        setOriginalAuthTokenSet(true);
        setAuthTokenChanged(false);
        setAuthToken("");
      } else {
        toast.error(result.error || "Failed to save SMS configuration");
      }
    } catch (error) {
      console.error("Error saving SMS config:", error);
      toast.error("Failed to save SMS configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    if (!currentTenant?.tenant_id) return;

    setVerifying(true);
    try {
      const result = await verifySmsConfig(currentTenant.tenant_id);

      if (result.valid) {
        toast.success(
          `Twilio connection verified! Phone: ${result.phone_number || phoneNumber}`
        );
        setVerified(true);
      } else {
        toast.error(result.error || "Could not verify Twilio credentials");
        setVerified(false);
      }
    } catch (error) {
      console.error("Error verifying SMS config:", error);
      toast.error("Failed to verify Twilio connection");
      setVerified(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSendTest = async () => {
    if (!currentTenant?.tenant_id) return;

    const cleaned = testPhone.trim();
    if (!cleaned) {
      toast.error("Enter a phone number to send the test SMS to");
      return;
    }

    // Basic E.164 validation
    if (!/^\+\d{10,15}$/.test(cleaned)) {
      toast.error("Enter a valid phone number in E.164 format (e.g., +15551234567)");
      return;
    }

    setSendingTest(true);
    try {
      const result = await sendTestSms(currentTenant.tenant_id, cleaned);

      if (result.success) {
        toast.success(`Test SMS sent to ${cleaned}`);
      } else {
        toast.error(result.error || "Failed to send test SMS");
      }
    } catch (error) {
      console.error("Error sending test SMS:", error);
      toast.error("Failed to send test SMS");
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const canSave =
    accountSid.trim() &&
    phoneNumber.trim() &&
    (authTokenChanged ? authToken.trim() : originalAuthTokenSet);

  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5 text-muted-foreground" />
                <div>
                  <CardTitle>SMS Configuration (Twilio)</CardTitle>
                  <CardDescription>
                    Connect your Twilio account to send and receive SMS messages
                    for {currentTenant?.company_name}
                  </CardDescription>
                </div>
              </div>
              <ChevronDown
                className={cn(
                  "h-5 w-5 text-muted-foreground transition-transform duration-200",
                  isExpanded && "rotate-180"
                )}
              />
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                You'll need a Twilio account with an active phone number. Find
                your Account SID and Auth Token on the{" "}
                <a
                  href="https://console.twilio.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Twilio Console dashboard
                </a>
                . SMS costs are billed by Twilio to your account.{" "}
                <a
                  href="https://www.twilio.com/en-us/sms/pricing/us"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  View pricing →
                </a>
              </AlertDescription>
            </Alert>

            <div className="grid gap-4">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="sms-enabled" className="text-sm font-medium">
                    Enable SMS Messaging
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Send and receive SMS messages with homeowners
                  </p>
                </div>
                <Switch
                  id="sms-enabled"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                />
              </div>

              {/* Account SID */}
              <div className="grid gap-2">
                <Label htmlFor="twilio-account-sid">Account SID</Label>
                <Input
                  id="twilio-account-sid"
                  value={accountSid}
                  onChange={(e) => setAccountSid(e.target.value)}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Found on your Twilio Console dashboard. Starts with "AC".
                </p>
              </div>

              {/* Auth Token */}
              <div className="grid gap-2">
                <Label htmlFor="twilio-auth-token">Auth Token</Label>
                <div className="relative">
                  <Input
                    id="twilio-auth-token"
                    type={showAuthToken ? "text" : "password"}
                    value={authToken}
                    onChange={(e) => {
                      setAuthToken(e.target.value);
                      setAuthTokenChanged(true);
                    }}
                    placeholder={
                      originalAuthTokenSet && !authTokenChanged
                        ? "Auth token saved (enter new to change)"
                        : "Enter your Twilio Auth Token"
                    }
                    className="pr-10 font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowAuthToken(!showAuthToken)}
                  >
                    {showAuthToken ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Found below your Account SID on the Twilio Console. This is
                  encrypted before storage.
                </p>
              </div>

              {/* Phone Number */}
              <div className="grid gap-2">
                <Label htmlFor="twilio-phone">Twilio Phone Number</Label>
                <Input
                  id="twilio-phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1XXXXXXXXXX"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Your Twilio phone number in E.164 format (e.g., +15551234567).
                  This is the number messages will be sent from.
                </p>
              </div>

              {/* Messaging Service SID (Optional) */}
              <div className="grid gap-2">
                <Label htmlFor="twilio-messaging-service">
                  Messaging Service SID{" "}
                  <span className="text-muted-foreground font-normal">
                    (Optional)
                  </span>
                </Label>
                <Input
                  id="twilio-messaging-service"
                  value={messagingServiceSid}
                  onChange={(e) => setMessagingServiceSid(e.target.value)}
                  placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Recommended for production use. Required for A2P 10DLC
                  compliance in the US. Starts with "MG".
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving || !canSave}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <CheckCircle className="mr-2 h-4 w-4" />
                Save Configuration
              </Button>

              <Button
                variant="outline"
                onClick={handleVerify}
                disabled={verifying || !originalAuthTokenSet}
              >
                {verifying && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {verified ? (
                  <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                ) : (
                  <Smartphone className="mr-2 h-4 w-4" />
                )}
                {verifying
                  ? "Verifying..."
                  : verified
                    ? "Verified"
                    : "Verify Connection"}
              </Button>
            </div>

            {/* Send Test SMS */}
            {originalAuthTokenSet && enabled && (
              <div className="p-4 border rounded-lg space-y-3">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Send Test SMS</Label>
                  <p className="text-sm text-muted-foreground">
                    Send a test message to verify your Twilio integration is
                    working end-to-end.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+15551234567"
                    className="font-mono text-sm max-w-[220px]"
                  />
                  <Button
                    variant="outline"
                    onClick={handleSendTest}
                    disabled={sendingTest || !testPhone.trim()}
                  >
                    {sendingTest ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    {sendingTest ? "Sending..." : "Send Test"}
                  </Button>
                </div>
              </div>
            )}

            {/* Status Preview */}
            {accountSid && phoneNumber && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">Configuration:</p>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    Account:{" "}
                    <span className="font-mono">
                      {accountSid.substring(0, 8)}...
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Phone Number:{" "}
                    <span className="font-mono">{phoneNumber}</span>
                  </p>
                  {messagingServiceSid && (
                    <p className="text-sm text-muted-foreground">
                      Messaging Service:{" "}
                      <span className="font-mono">
                        {messagingServiceSid.substring(0, 8)}...
                      </span>
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Status:{" "}
                    <span
                      className={cn(
                        "font-medium",
                        enabled ? "text-green-600" : "text-yellow-600"
                      )}
                    >
                      {enabled ? "Enabled" : "Disabled"}
                    </span>
                    {verified && (
                      <span className="ml-2 text-green-600">
                        <CheckCircle className="inline h-3.5 w-3.5 mr-1" />
                        Verified
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
